import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, inject, Input, NgZone, OnChanges } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { RedirectHelper } from '@app/app/page-modules/resource/store/editor/content/helpers/redirect.helper';
import { playlistCardsReservedRows } from '@app/app/shared/collections/card-item/card-item-content-card.mapper';
import { V3ContentCardRow } from '@app/app/shared/components-v3/content-card/v3-content-card.model';
import { resolveContentEntityType } from '@app/app/shared/helpers/content-entity-type.helper';
import { NestedPathHelper } from '@app/app/shared/helpers/nested-path.helper';
import { CardsLayoutType, PlaylistCardShort } from '@app/app/shared/models';
import { isWebLinkCard, WebLinkCardService } from '@app/app/shared/services/card/web-link-card.service';
import { TranslocoService } from '@ngneat/transloco';
import { map } from 'rxjs';

// A single column has no zig-zag to draw, so phones get the plain card grid instead of the snake.
export const SNAKE_UNAVAILABLE_LAYOUT = '(max-width: 699.98px)';

export function snakeUnavailable(): () => boolean {
  return toSignal(
    inject(BreakpointObserver)
      .observe(SNAKE_UNAVAILABLE_LAYOUT)
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );
}

@Component({
  selector: 'ptl-collection-cards-view',
  templateUrl: './collection-cards-view.component.html',
  styleUrls: ['./collection-cards-view.component.scss'],
})
export class CollectionCardsViewComponent implements OnChanges {
  @Input() cards: PlaylistCardShort[] = [];
  @Input() currentLayoutView: CardsLayoutType = 'CardView';

  reservedRows: V3ContentCardRow[] = [];

  private readonly isSnakeUnavailable = snakeUnavailable();

  get layoutView(): CardsLayoutType {
    return this.currentLayoutView === 'SnakeView' && this.isSnakeUnavailable() ? 'CardView' : this.currentLayoutView;
  }

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private activatedRoute: ActivatedRoute,
    private transloco: TranslocoService,
    private webLinkCardService: WebLinkCardService,
  ) {}

  ngOnChanges(): void {
    const view = this.currentLayoutView === 'ListView' || this.currentLayoutView === 'NumberedListView' ? 'list' : 'grid';
    this.reservedRows = playlistCardsReservedRows(this.cards, this.transloco, view);
  }

  onCardClick(card: PlaylistCardShort): void {
    if (isWebLinkCard(card)) {
      this.webLinkCardService.open(card);
      return;
    }

    const uri = NestedPathHelper.resolveCardSlug(card);
    if (!uri) {
      return;
    }

    const currentUrl = this.router.url.split('?')[0];
    const entityType = resolveContentEntityType(card);

    // Assignment cards open their own view at the bare nested URL (no tab suffix).
    if (entityType === 'assignment') {
      const assignmentPath = NestedPathHelper.buildPathForEntity('assignment', uri, NestedPathHelper.getAllSegments(currentUrl));
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, assignmentPath);
      return;
    }

    // Outside a nested path this yields the entity's own top-level route.
    const newPath = NestedPathHelper.addEntityToPath(currentUrl, entityType, uri, 'content');
    RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, newPath);
  }
}
